import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";

/**
 * Active-workspace context — drives which workspace the dashboard
 * is currently scoped to. A user can have multiple workspaces:
 *   • One or more team workspaces (where the work happens)
 *   • Exactly one personal workspace (private, solo)
 *
 * The active workspace controls:
 *   • Which teamId the dashboard queries scope to
 *   • Whether manager-only AppHeader buttons are shown
 *   • Whether the "Personal workspace" badge renders
 *
 * Persistence: localStorage `activeWorkspaceId`. URL param
 * `?ws=<id>` takes precedence on first load (deep-link support
 * from the team picker UI or external links).
 *
 * Default fallback: the first non-personal team in the user's
 * list. Personal becomes active only when the user explicitly
 * selects it — never by default, so existing users see no change.
 */
export type Workspace = {
  team: {
    id: number;
    name: string;
    isPersonal: boolean;
    isContainer: boolean;
    parentTeamId: number | null;
  };
  role: "owner" | "manager" | "lead" | "member";
};

type Ctx = {
  /** All workspaces the user can switch into (incl. personal). */
  workspaces: Workspace[];
  /** Currently active workspace, or null until data loads. */
  active: Workspace | null;
  /** Switch to a different workspace by id. Persists choice. */
  setActiveId: (workspaceId: number) => void;
  /** True if more than one workspace exists (picker should render). */
  hasMultiple: boolean;
  /** Loading flag — UI can show a skeleton until workspaces load. */
  isLoading: boolean;
};

const LS_KEY = "receipt:activeWorkspaceId";

const ActiveWorkspaceContext = createContext<Ctx>({
  workspaces: [],
  active: null,
  setActiveId: () => {},
  hasMultiple: false,
  isLoading: false,
});

export function useActiveWorkspace() {
  return useContext(ActiveWorkspaceContext);
}

export function ActiveWorkspaceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  // New-user bootstrap (org + personal workspace) is now guaranteed
  // SERVER-SIDE inside `team.myWorkspaces` (see ensureUserBootstrap),
  // so this query already returns at least the personal workspace for a
  // brand-new user. The former client-side `team.ensureMine` hack here
  // was removed in PASS C — the server is the single source of truth.
  const workspacesQ = trpc.team.myWorkspaces.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  // Initial pick: URL param > localStorage > first non-personal.
  // Computed once when workspaces data lands. Subsequent changes go
  // through setActiveId.
  const [activeId, setActiveIdState] = useState<number | null>(null);

  useEffect(() => {
    if (!workspacesQ.data || workspacesQ.data.length === 0) return;
    if (activeId != null) {
      // Already chosen; verify the id is still in the list. If the
      // user lost access (e.g. removed from a team), fall back to
      // first non-personal.
      const stillValid = workspacesQ.data.some(w => w.team.id === activeId);
      if (stillValid) return;
    }
    // First-load resolution:
    const urlMatch =
      typeof window !== "undefined"
        ? /[?&]ws=(\d+)/.exec(window.location.search)
        : null;
    const fromUrl = urlMatch ? Number(urlMatch[1]) : null;
    const fromLS =
      typeof window !== "undefined"
        ? Number(window.localStorage.getItem(LS_KEY) ?? "0") || null
        : null;
    const validIds = new Set(workspacesQ.data.map(w => w.team.id));
    const firstNonPersonal =
      workspacesQ.data.find(w => !w.team.isPersonal)?.team.id ??
      workspacesQ.data[0]?.team.id ??
      null;
    const resolved =
      (fromUrl && validIds.has(fromUrl) ? fromUrl : null) ??
      (fromLS && validIds.has(fromLS) ? fromLS : null) ??
      firstNonPersonal;
    setActiveIdState(resolved);
  }, [workspacesQ.data, activeId]);

  const setActiveId = useCallback((workspaceId: number) => {
    setActiveIdState(workspaceId);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LS_KEY, String(workspaceId));
    }
  }, []);

  const active = useMemo(() => {
    if (!workspacesQ.data || activeId == null) return null;
    return workspacesQ.data.find(w => w.team.id === activeId) ?? null;
  }, [workspacesQ.data, activeId]);

  const value = useMemo<Ctx>(
    () => ({
      workspaces: workspacesQ.data ?? [],
      active,
      setActiveId,
      hasMultiple: (workspacesQ.data?.length ?? 0) > 1,
      isLoading: workspacesQ.isLoading,
    }),
    [workspacesQ.data, workspacesQ.isLoading, active, setActiveId],
  );

  return (
    <ActiveWorkspaceContext.Provider value={value}>
      {children}
    </ActiveWorkspaceContext.Provider>
  );
}
